import React, { useEffect, useState } from 'react';
import BaseInput from 'components/atoms/base-input';
import BaseSelectBox from 'components/atoms/base-select-box';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';

import type { TUseMedicationsFilterReturn } from './use-medications-filter';

interface MedicationsFilterProps {
  filterState: TUseMedicationsFilterReturn;
}

const AdminMedicationsFilter: React.FC<MedicationsFilterProps> = ({
  filterState,
}) => {
  const { t } = useTranslation();
  const { filter, updateFilter, resetFilter, count } = filterState;
  const [searchValue, setSearchValue] = useState(filter.search ?? '');

  useEffect(() => {
    setSearchValue(filter.search ?? '');
  }, [filter.search]);

  useEffect(() => {
    const trimmedSearch = searchValue?.trim() ? searchValue : undefined;
    if (trimmedSearch === filter.search) {
      return;
    }

    const handler = window.setTimeout(() => {
      updateFilter({
        search: trimmedSearch,
        page: 1,
      });
    }, 400);

    return () => window.clearTimeout(handler);
  }, [searchValue, filter.search]);

  const handleReset = () => {
    resetFilter();
  };

  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={handleReset}
      openWithValue
      isExpanded
    >
      <div className={s.wrapper}>
        <ExpandableFilterPanelItem label={t('labels.searchMedications')}>
          <BaseInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t('labels.searchMedicationsPlaceholder')}
            allowClear
          />
        </ExpandableFilterPanelItem>

        <ExpandableFilterPanelItem label={t('labels.otc')}>
          <BaseSelectBox
            value={filter.otc}
            onChange={(value) =>
              updateFilter({
                otc: (value ?? 'all') as typeof filter.otc,
                page: 1,
              })
            }
            options={[
              { value: 'all', label: t('labels.all') },
              { value: 'yes', label: t('labels.yes') },
              { value: 'no', label: t('labels.no') },
            ]}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.papAvailable')}>
          <BaseSelectBox
            value={filter.papAvailability}
            onChange={(value) =>
              updateFilter({
                papAvailability: (value ??
                  'all') as typeof filter.papAvailability,
                page: 1,
              })
            }
            options={[
              { value: 'all', label: t('labels.all') },
              { value: 'available', label: t('labels.available') },
              { value: 'not_available', label: t('labels.notAvailable') },
            ]}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};

export default AdminMedicationsFilter;
